/**
 * Documents player — viewport scroll + optional dwell time → Mark done
 */

import * as React from 'react';
import { cn, Button } from '@ai-content-hub/content-kit';
import { PagePlayer } from '@ai-content-hub/content-elements';
import { useLocale, useContentPlayerActions } from '@ai-content-hub/content-core';
import type { DocumentsData, DocumentsConfig, DocumentsAnswer } from './schema';
import { defaultDocumentsData, normalizeDocumentsCompletion } from './schema';

export interface DocumentsPlayerProps {
    data: DocumentsData;
    config?: DocumentsConfig;
    answer?: DocumentsAnswer | null;
    onAnswer?: (answer: DocumentsAnswer) => void;
    showFeedback?: boolean;
    isReview?: boolean;
    disabled?: boolean;
    className?: string;
    result?: { isCorrect?: boolean | null };
}

/** Skips re-renders when only dwell/scroll state in the parent updates (1s timer). */
const DocumentsPlayerPageSection = React.memo(function DocumentsPlayerPageSection({
    pageContent,
}: {
    pageContent: DocumentsData['pageContent'];
}) {
    return (
        <div className="documents-player-body p-4">
            <PagePlayer data={pageContent} className="documents-player-page" />
        </div>
    );
});

export const DocumentsPlayer: React.FC<DocumentsPlayerProps> = ({
    data,
    config,
    answer,
    onAnswer,
    showFeedback = false,
    isReview = false,
    disabled = false,
    className,
}) => {
    const playerActions = useContentPlayerActions();
    const docCfg = normalizeDocumentsCompletion(config?.documents);
    const { t } = useLocale({ config: config?.i18n });

    const safeData: DocumentsData = React.useMemo(
        () => ({
            ...defaultDocumentsData,
            ...data,
            pageContent: data?.pageContent ?? defaultDocumentsData.pageContent,
        }),
        [data]
    );

    const viewportRef = React.useRef<HTMLDivElement>(null);
    const [scrolledToEnd, setScrolledToEnd] = React.useState(false);
    const [dwellSeconds, setDwellSeconds] = React.useState(0);
    const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

    const handleScrollCheck = React.useCallback(() => {
        const el = viewportRef.current;
        if (!el || !docCfg.requireFullScroll) return;
        const slack = 24;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - slack) {
            setScrolledToEnd(true);
        }
    }, [docCfg.requireFullScroll]);

    React.useEffect(() => {
        const el = viewportRef.current;
        if (!docCfg.requireFullScroll || !el) return undefined;
        handleScrollCheck();
        const ro = new ResizeObserver(() => handleScrollCheck());
        ro.observe(el);
        return () => ro.disconnect();
    }, [docCfg.requireFullScroll, safeData.pageContent, handleScrollCheck]);

    React.useEffect(() => {
        if (isReview || showFeedback || disabled) return undefined;

        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }
            setDwellSeconds((s) => s + 1);
        };

        intervalRef.current = setInterval(tick, 1000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        };
    }, [disabled, isReview, showFeedback]);

    const minNeeded = docCfg.minimumViewSeconds > 0 ? docCfg.minimumViewSeconds : 0;
    const dwellOk = minNeeded <= 0 || dwellSeconds >= minNeeded;
    const scrollOk = !docCfg.requireFullScroll || scrolledToEnd;
    const gatesOk = dwellOk && scrollOk;

    const showMarkFooter = !showFeedback && !isReview && !disabled;
    const canMark = gatesOk;

    const handleMarkDone = () => {
        if (!canMark) return;
        const payload: DocumentsAnswer = {
            completed: true,
            viewedSeconds: dwellSeconds,
            scrolledToEnd: scrollOk ? true : false,
        };
        onAnswer?.(payload);
        playerActions?.requestSubmit();
    };

    return (
        <div className={cn('documents-player flex flex-col h-full min-h-0 gap-4', className)}>
            <div
                ref={viewportRef}
                onScroll={handleScrollCheck}
                className="documents-player-viewport flex-1 min-h-0 overflow-y-auto rounded-lg border border-border bg-card shadow-sm"
            >
                {(safeData.description ?? '').trim() ? (
                    <div className="documents-player-header border-b border-border px-4 py-3 shrink-0">
                        <p className="documents-player-description mt-1 text-muted-foreground text-sm">
                            {safeData.description}
                        </p>
                    </div>
                ) : null}
                <DocumentsPlayerPageSection pageContent={safeData.pageContent} />
            </div>

            {(showFeedback || isReview) && answer?.completed ? (
                <div className="documents-player-feedback text-success text-sm font-medium px-1">
                    {t('completed', 'Completed')}
                </div>
            ) : null}

            {showMarkFooter ? (
                <div className="documents-player-actions flex-shrink-0 border-t border-border pt-3 flex flex-col gap-2">
                    {(docCfg.requireFullScroll || minNeeded > 0) && !gatesOk ? (
                        <div className="documents-player-gates text-sm text-muted-foreground space-y-1">
                            {docCfg.requireFullScroll && !scrolledToEnd ? (
                                <p>
                                    {t(
                                        'scrollHint',
                                        'Scroll to the bottom of the document to continue.'
                                    )}
                                </p>
                            ) : null}
                            {minNeeded > 0 && dwellSeconds < minNeeded ? (
                                <p>
                                    {t(
                                        'dwellHint',
                                        'Stay on this activity for at least {{seconds}}s (visible tab).'
                                    ).replace('{{seconds}}', String(minNeeded))}{' '}
                                    ({dwellSeconds}s / {minNeeded}s)
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                    <Button
                        type="button"
                        className="documents-player-mark-done w-full cursor-pointer"
                        disabled={!canMark}
                        onClick={handleMarkDone}
                    >
                        {t('markDone', 'Mark done')}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};
